// Pagination-aware Messages component with responsive mobile-first enhancements
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';


import './Messages.css';
import Sidebar from './../../components/Sidebar';
import NavBar from './../../components/Navbar';
import Pagination from './../../components/Pagination';
import api from './../../api/axios';
import Userfront from "../../auth/userfrontCompat";
import { useTranslation } from 'react-i18next';
import { Modal, Button, Form } from 'react-bootstrap';

const leadsPerPageDefault = 10;

const Messages = () => {
  const [leadsPerPage, setLeadsPerPage] = useState(leadsPerPageDefault);
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [notification, setNotification] = useState(null);
  const { t, i18n } = useTranslation();
  const [userLanguage, setUserLanguage] = useState('en');
  const [isMobile, setIsMobile] = useState(false);
const [mobileView, setMobileView] = useState('list');
const [showAssistant, setShowAssistant] = useState(false);

  const messageEndRef = useRef(null);

  const [recordsPerPage, setRecordsPerPage] = useState(10); // Default records per page

  const [aiPrompt, setAiPrompt] = useState('');
  const [systemInstruction, setSystemInstruction] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  


  const openAssistant = () => {
    setShowAssistant(true);
    if (isMobile) setMobileView('assistant');
  };

  const closeAssistant = () => {
    setShowAssistant(false);
    if (isMobile) setMobileView('chat');
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeAssistant();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMobile]);


  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchLeads();
    fetchUserLanguage();
  }, []);

  useEffect(() => {
    // After leads load, select the lead from redirect (if provided)
    if (leads.length > 0 && location.state?.leadName) {
      fetchMessages(location.state.leadName);
      navigate('/messages', { replace: true }); // Clears state
    }
  }, [leads, location.state]);


  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const fetchUserLanguage = async () => {
    try {
      const response = await api.get(`/user/user`);
      setUserLanguage(response.data.language);
      i18n.changeLanguage(response.data.language);
    } catch (error) {
      console.error('Error fetching user settings:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await api.get(`/messages/leads/${Userfront.user.email}`);
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const fetchMessages = async (leadName) => {
    try {
      const lead = leads.find((l) => l.leadName === leadName);
      if (!lead) return;

      const response = await api.get(`/messages/messages/${encodeURIComponent(leadName)}?email=${Userfront.user.email}`);
      setMessages(response.data);
      setSelectedLead(lead);
      setMobileView(window.innerWidth < 768 ? 'chat' : mobileView);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to the first page when changing records per page
  };

  const handleSendMessage = async () => {
    setShowConfirmation(false);
    try {
      const lead = leads.find((lead) => lead.leadName === selectedLead?.leadName);
      if (!lead) return;

      setNotification('sending');

      await api.post('/messages/add', {
        leadId: lead.leadId,
        leadName: selectedLead.leadName,
        sender: Userfront.user.email,
        content: userMessage,
      });

      setUserMessage('');
      setAiResponse('');
      fetchMessages(selectedLead.leadName);
      setNotification('sent');
    } catch (error) {
      console.error('Error sending message:', error);
      setNotification('error');
    }
  };

  const handleGenerateAiResponse = async () => {
    if (!systemInstruction.trim()) return;

    try {
      const response = await api.post('/integrations/openai/messages/generate-response', {
        leadName: selectedLead.leadName,
        conversation: messages,
        systemInstruction,
        aiPrompt,
      });

      setAiResponse(response.data.aiResponse);
    } catch (error) {
      console.error('Error generating AI response:', error);
    }
  };

  const handleRefineAiResponse = async () => {
    if (!aiPrompt.trim() || !systemInstruction.trim()) return;

    try {
      const formattedMessages = messages.map(msg => ({
        sender: msg.sender === Userfront.user.email ? 'user' : 'lead',
        text: msg.text
      }));

      const response = await api.post('/integrations/openai/messages/generate-response', {
        leadName: selectedLead.leadName,
        conversation: formattedMessages,
        aiPrompt,
        systemInstruction,
        previousAiResponse: aiResponse,
        refine: true
      });

      setAiResponse(response.data.aiResponse);
    } catch (error) {
      console.error('Error refining AI response:', error);
    }
  };

  const filteredLeads = leads.filter((lead) =>
    lead.leadName && lead.leadName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredLeads.length / leadsPerPage);
  const paginatedLeads = filteredLeads.slice((currentPage - 1) * leadsPerPage, currentPage * leadsPerPage);

  return (
  <div className="border-t border-gray-200">
    <div className="min-h-screen bg-gray-50 transition-all space-y-6 px-2 pt-2 sm:px-3 md:px-4 xl:px-8 pb-6">
      <div className="space-y-6 px-2 sm:px-3 md:px-4 lg:px-6 pt-2">

        

        <div className="relative">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Lead List */}
            {(!isMobile || mobileView === 'list') && (
              <div className="w-full md:w-[320px] bg-white border rounded-xl p-4 shadow-sm">
                <h2 className="text-xl font-semibold mb-3">{t('messages.inboxTitle')}</h2>
                <input
                  type="text"
                  placeholder={t('messages.searchPlaceholder')}
                  className="w-full border px-3 py-2 rounded-md text-sm mb-4"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                <ul className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                  {paginatedLeads.map((lead, index) => (
                    <li
                      key={index}
                      className={`flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-gray-100 ${
                        selectedLead?.leadName === lead.leadName ? 'bg-blue-100 border border-blue-300' : ''
                      }`}
                      onClick={() => fetchMessages(lead.leadName)}
                    >
                      {lead.profileImageUrl ? (
                        <img
                          src={lead.profileImageUrl}
                          alt={t('messages.leadAvatarAlt')}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center font-bold text-white">
                          {lead.leadName.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="text-sm font-medium text-gray-700 truncate">{lead.leadName}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 space-y-2">
                  <div className="text-sm text-gray-600 flex items-center gap-2">
                    <span>{t('leads.rowsPerPage')}</span>
                    <select
                      value={recordsPerPage}
                      onChange={handleRecordsPerPageChange}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>

                  <Pagination
                    currentPage={currentPage}
                    totalItems={filteredLeads.length}
                    recordsPerPage={leadsPerPage}
                    paginate={(page) => setCurrentPage(page)}
                  />
                </div>
              </div>
            )}

            {/* Chat Panel */}
            {(!isMobile || mobileView === 'chat') && selectedLead && (
              <div className="flex-1 bg-white border rounded-xl p-4 shadow-sm min-w-0">
                <div className="flex justify-between items-center mb-4">
                  {isMobile && (
                    <button onClick={() => setMobileView('list')} className="text-sm text-blue-600">
                      ← {t('messages.backToInbox')}
                    </button>
                  )}

                  <div className="flex items-center gap-3 min-w-0">
                    {selectedLead?.profileImageUrl ? (
                      <img
                        src={selectedLead.profileImageUrl}
                        alt={t('messages.leadAvatarAlt')}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center font-bold text-white">
                        {selectedLead?.leadName?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <h2 className="text-lg font-semibold truncate">{selectedLead?.leadName}</h2>
                  </div>

                  <button
                    onClick={() => (showAssistant ? closeAssistant() : openAssistant())}
                    className="text-sm text-gray-600 border border-gray-300 rounded px-2 py-1"
                    title="AI assistant"
                  >
                    🤖
                  </button>
                </div>

                <div className="space-y-3 overflow-y-auto max-h-[50vh] pr-2" id="messageList">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`p-2 rounded-md max-w-[70%] ${
                        message.sender === Userfront.user.email ? 'bg-blue-100 self-end ml-auto' : 'bg-gray-100'
                      }`}
                    >
                      <div className="text-xs font-semibold text-gray-500 mb-1">{message.sender}</div>
                      <div className="text-sm text-gray-800 whitespace-pre-wrap">{message.text}</div>
                    </div>
                  ))}
                  <div ref={messageEndRef} />
                </div>

                <textarea
                  className="w-full border rounded-md p-2 mt-4 text-sm resize-none max-h-40 overflow-auto"
                  value={userMessage}
                  onChange={(e) => {
                    setUserMessage(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  rows={3}
                  placeholder={t('messages.writeMessagePlaceholder')}
                />

                <div className="text-right mt-2">
                  <button
                    onClick={() => setShowConfirmation(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm"
                  >
                    {t('messages.sendMessageButton')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* AI Drawer Overlay (desktop + mobile) */}
          {(showAssistant || mobileView === 'assistant') && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black/30 z-40"
                onClick={closeAssistant}
              />

              {/* Drawer */}
              <div
                className={`
                  fixed top-0 right-0 h-full z-50 bg-white border-l shadow-xl
                  w-full sm:w-[420px]
                  transform transition-transform duration-200
                  ${showAssistant || mobileView === 'assistant' ? 'translate-x-0' : 'translate-x-full'}
                `}
              >
                <div className="h-full flex flex-col">
                  {/* Drawer Header */}
                  <div className="p-4 border-b flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="text-sm text-gray-500">AI Assistant</div>
                      <div className="font-semibold truncate">
                        {selectedLead?.leadName ? `For: ${selectedLead.leadName}` : 'Select a lead'}
                      </div>
                    </div>
                    <button
                      onClick={closeAssistant}
                      className="text-sm text-gray-600 border border-gray-300 rounded px-2 py-1"
                    >
                      ✕
                    </button>
                  </div>

                  {/* Drawer Body */}
                  <div className="p-4 overflow-y-auto flex-1">
                    <textarea
                      className="w-full border rounded-md p-2 text-sm mb-3"
                      placeholder="Geef instructie voor de AI om het antwoord te starten..."
                      value={systemInstruction}
                      onChange={(e) => setSystemInstruction(e.target.value)}
                    />

                    <button
                      onClick={handleGenerateAiResponse}
                      disabled={!systemInstruction.trim() || !selectedLead}
                      className={`w-full px-4 py-2 rounded-md text-sm mb-4 ${
                        !systemInstruction.trim() || !selectedLead
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white'
                      }`}
                    >
                      {t('messages.generateAiResponseButton')}
                    </button>

                    {aiResponse && (
                      <div className="space-y-3">
                        <h3 className="text-base font-semibold">{t('messages.aiResponseTitle')}</h3>
                        <div className="text-sm text-gray-700 whitespace-pre-wrap border rounded-md p-3 bg-gray-50">
                          {aiResponse}
                        </div>

                        <textarea
                          className="w-full border rounded-md p-2 text-sm"
                          placeholder="Geef een suggestie om dit antwoord te verbeteren..."
                          value={aiPrompt}
                          onChange={(e) => setAiPrompt(e.target.value)}
                        />

                        <div className="flex gap-2">
                          <button
                            onClick={handleRefineAiResponse}
                            disabled={!aiPrompt.trim() || !systemInstruction.trim()}
                            className={`flex-1 text-sm px-3 py-2 rounded-md ${
                              !aiPrompt.trim() || !systemInstruction.trim()
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                : 'bg-gray-200 hover:bg-gray-300'
                            }`}
                          >
                            Verfijn
                          </button>

                          <button
                            onClick={() => setUserMessage(aiResponse)}
                            className="flex-1 bg-gray-200 hover:bg-gray-300 text-sm px-3 py-2 rounded-md"
                          >
                            Kopieer
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Drawer Footer (optional quick actions) */}
                  <div className="p-4 border-t text-xs text-gray-500">
                    Tip: druk op <span className="font-semibold">Esc</span> om te sluiten.
                  </div>
                </div>
              </div>
            </>
          )}
        </div>


        {/* Confirmation Modal */}
        {showConfirmation && (
          <div className="fixed inset-0 bg-white/40 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-gray-100 w-full max-w-md p-6 rounded-xl shadow-xl border">
              <h3 className="text-lg font-semibold mb-4">{t('messages.confirmSend')}</h3>
              <p className="text-sm text-gray-700 mb-6">{t('messages.confirmSendBody')}</p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md text-sm"
                >
                  {t('messages.cancel')}
                </button>
                <button
                  onClick={handleSendMessage}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm"
                >
                  {t('messages.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);


};

export default Messages;
